"use client";
import React from "react";
import { motion } from "framer-motion";
import { useTheme } from "../../Components/ThemeContext";

const lightGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const darkGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.03) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

export default function MilestonesAchievements() {
  const { isDark } = useTheme();

  // ✅ SEO: keyword-rich milestone content — targets: software development company,
  // web development, AI automation, digital transformation, startups, UK
  const milestones = [
    {
      year: "2025",
      label: "Founded",
      text: "Fiieve was founded in 2025 with a clear mission — to deliver high-performance web development, mobile apps, and AI automation solutions for startups and growing businesses across the UK and beyond.",
    },
    {
      year: "2025",
      label: "First 10 Projects",
      text: "Successfully delivered 10+ software development projects across e-commerce, fintech, healthtech, and service industries — helping clients achieve measurable digital transformation and online growth.",
    },
    {
      year: "2025",
      label: "Global Reach",
      text: "Expanded to support clients across multiple regions, providing end-to-end software engineering, UI/UX design, and IT consultancy services — building long-term partnerships based on trust, transparency, and results.",
    },
    {
      year: "2025+",
      label: "Growing Reputation",
      text: "Earned consistent client appreciation for quality, on-time delivery, and technical excellence — continuously raising the bar in web development, AI-powered solutions, and embedded systems engineering.",
    },
  ];

  const greenHeading = isDark ? "text-green-400" : "text-green-700";
  const greenText    = isDark ? "text-green-400" : "text-green-700";
  const cardBg       = isDark ? "bg-gray-900"    : "bg-[#1a2e1a]";
  const dotBorder    = isDark ? "border-black"   : "border-transparent";

  return (
    // ✅ SEO: <section> with aria-labelledby replaces <div> — meaningful landmark
    <section
      aria-labelledby="milestones-heading"
      className={`relative overflow-hidden flex items-center justify-center py-10 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark ? "bg-black text-white" : "bg-transparent text-gray-900"
      }`}
      style={isDark ? darkGridStyle : lightGridStyle}
    >
      {/* Light mode glows */}
      {!isDark && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
          <div style={{
            position: "absolute", top: "0%", left: "-8%",
            width: "45vw", height: "45vw",
            background: "radial-gradient(circle, rgba(74,222,128,0.10) 0%, transparent 70%)",
            filter: "blur(45px)",
          }} />
          <div style={{
            position: "absolute", top: "0%", right: "-8%",
            width: "40vw", height: "40vw",
            background: "radial-gradient(circle, rgba(251,146,60,0.08) 0%, transparent 70%)",
            filter: "blur(45px)",
          }} />
        </div>
      )}

      {isDark && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
          <div style={{
            position: "absolute", top: "10%", left: "10%",
            width: "50vw", height: "50vw",
            background: "radial-gradient(circle, rgba(74,222,128,0.05) 0%, transparent 70%)",
            filter: "blur(45px)",
          }} />
          <div style={{
            position: "absolute", bottom: "0%", right: "5%",
            width: "40vw", height: "40vw",
            background: "radial-gradient(circle, rgba(251,146,60,0.04) 0%, transparent 70%)",
            filter: "blur(45px)",
          }} />
        </div>
      )}

      <div className="relative w-full max-w-4xl" style={{ zIndex: 1 }}>

        {/* ✅ SEO: label above heading for context */}
        <p className={`text-sm font-semibold uppercase tracking-widest text-center ${greenText} mb-1`}>
          Our Journey
        </p>

        {/* ✅ SEO: H2 with id — keyword-rich, correct hierarchy */}
        <h2
          id="milestones-heading"
          className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${greenHeading} mb-8 sm:mb-10 text-center`}
        >
          Milestones &{" "}
          <span className={isDark ? "text-white" : "text-gray-900"}>Achievements</span>
        </h2>

        {/* ✅ SEO: <ol> — ordered list signals a sequential timeline to crawlers */}
        <ol className="relative" aria-label="Fiieve company milestones timeline">

          {/* Vertical line */}
          <div className="hidden sm:block absolute left-6 top-0 bottom-10 w-0.5 bg-green-400" aria-hidden="true" />

          <div className="space-y-6 sm:space-y-8">
            {milestones.map((milestone, index) => (
              <motion.li
                key={index}
                className="relative flex items-start gap-4 sm:gap-6 list-none"
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.2, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                {/* Circle dot */}
                <div
                  className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 bg-green-400 rounded-full border-4 ${dotBorder} flex items-center justify-center z-10`}
                  aria-hidden="true"
                >
                  <div className="w-3 h-3 bg-black rounded-full" />
                </div>

                {/* Content card */}
                <motion.div
                  className={`flex-1 border-2 border-transparent hover:border-green-400 rounded-lg p-4 sm:p-6 ${cardBg} hover:shadow-lg hover:shadow-green-400/20 transition-all duration-300`}
                  whileHover={{ scale: 1.03 }}
                >
                  {/* ✅ SEO: H3 label for each milestone — crawlable heading hierarchy */}
                  <h3 className={`text-sm font-bold uppercase tracking-widest ${greenText} mb-1`}>
                    {milestone.year} — {milestone.label}
                  </h3>
                  <p className="text-white text-sm sm:text-base leading-relaxed">
                    {milestone.text}
                  </p>
                </motion.div>
              </motion.li>
            ))}
          </div>
        </ol>
      </div>
    </section>
  );
}